<!--
  Close only when the backdrop itself is clicked (target === the backdrop),
  NOT via stopPropagation on the panel. Stopping propagation here would prevent
  inner clicks from reaching document:click, which is what the contact picker's
  (clickOutside) relies on to close its dropdown.
-->
<div class="ext-gen-backdrop" *ngIf="visible" (click)="onBackdropClick($event)">
  <div class="ext-gen-modal">
    <div class="ext-gen-modal__head">
      <h3>{{ title }}</h3>
      <button type="button" class="ext-gen-modal__close" (click)="close()" aria-label="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <div class="ext-gen-modal__body">
      <p class="ext-gen-modal__intro" *ngIf="!isEditing">Generate a secure link for a buyer to complete this project's EOI form.</p>
      <p class="ext-gen-modal__intro" *ngIf="isEditing">
        Update this link's details. The buyer keeps the URL they already have — use "Generate new link" instead if you need to invalidate it.
      </p>

      <div class="ext-gen-field">
        <label>Project</label>
        <!-- Callers with a project of their own show it read-only; a contact has
             several (or none), so there the agent picks. -->
        <input *ngIf="!needsProjectPicker" type="text" [value]="projectName || '—'" disabled />
        <ng-select
          *ngIf="needsProjectPicker"
          class="ext-gen-select"
          [items]="projects"
          bindLabel="Project_Name"
          bindValue="_id"
          [searchable]="true"
          [clearable]="true"
          [loading]="projectsLoading"
          [placeholder]="projectsLoading ? 'Loading projects…' : 'Select a project'"
          [(ngModel)]="genProjectId"
          (ngModelChange)="onProjectChange()">
        </ng-select>
      </div>

      <div class="ext-gen-field">
        <label>Lot</label>
        <!-- Locked when creating from a record that already fixed the lot. -->
        <input *ngIf="lotLocked" type="text" [value]="lotName || 'Selected lot'" disabled />
        <!-- Optional — a form is often sent before a lot has been agreed, so the
             picker stays clearable and an empty value is a valid answer. -->
        <ng-select
          *ngIf="!lotLocked"
          class="ext-gen-select"
          [items]="lots"
          bindLabel="lot_label"
          bindValue="_id"
          [searchable]="true"
          [clearable]="true"
          [loading]="lotsLoading"
          [placeholder]="lotsLoading ? 'Loading lots…' : 'Select Lot'"
          [(ngModel)]="genLotId">
        </ng-select>
      </div>

      <div class="ext-gen-field">
        <label>Buyer / Purchaser</label>

        <!-- Opened from a record that already names the buyer (a lead or an EOI):
             the link is for that person, so the pickers are replaced with a
             read-only summary. Making the agent find the person they just came
             from is busywork, and re-picking them out of the lead list is what
             lost the email address — that list carries no addresses. -->
        <div class="ext-gen-prefilled" *ngIf="hasPrefilledBuyer">
          <i class="fa-solid fa-user"></i>
          <div>
            <strong>{{ prefilledBuyerName }}</strong>
            <span *ngIf="buyerEmail">{{ buyerEmail }}</span>
          </div>
        </div>

        <div class="ext-gen-toggle" *ngIf="!hasPrefilledBuyer">
          <button type="button" [class.is-active]="buyerMode === 'existing'" (click)="setBuyerMode('existing')">Contact</button>
          <button type="button" [class.is-active]="buyerMode === 'lead'" (click)="setBuyerMode('lead')">Lead</button>
          <button type="button" [class.is-active]="buyerMode === 'new'" (click)="setBuyerMode('new')">New Contact</button>
        </div>

        <ng-container *ngIf="!hasPrefilledBuyer && buyerMode === 'existing'">
          <!-- requireEmail: the passcode this link depends on can only be emailed, so a contact
               with none on file is not a pickable answer here — no point listing it. -->
          <re-multiselect
            [module]="'contacts'"
            [placeholder]="'Search contacts'"
            [isMultiple]="false"
            [isCreateNew]="true"
            [isFullObject]="true"
            [isEmail]="true"
            [requireEmail]="true"
            [selected_ids]="genContactIds"
            (onUpdate)="onContactSelect($event)"
            (createNew)="onContactCreate($event)"></re-multiselect>
        </ng-container>

        <!-- Leads are searchable in their own right: a link is very often sent
             to someone who is a lead and not yet a contact. -->
        <ng-container *ngIf="!hasPrefilledBuyer && buyerMode === 'lead'">
          <re-multiselect
            [module]="'leads'"
            [placeholder]="'Search leads'"
            [isMultiple]="false"
            [isFullObject]="true"
            [isEmail]="true"
            [requireEmail]="true"
            [selected_ids]="genContactIds"
            (onUpdate)="onContactSelect($event)"></re-multiselect>
        </ng-container>

        <ng-container *ngIf="!hasPrefilledBuyer && buyerMode === 'new'">
          <input type="text" class="mb-2" placeholder="Full name" [(ngModel)]="newBuyerName" />
          <input type="email" class="mb-2" placeholder="Email address" [(ngModel)]="newBuyerEmail" />
          <input type="tel" placeholder="Mobile (optional)" [(ngModel)]="newBuyerMobile" />
        </ng-container>

        <!-- The one-time passcode is emailed, so without an address the buyer cannot open the
             form. Held back until a buyer has been chosen: on an untouched dialog this was an
             error about a field the agent had not reached yet. -->
        <small class="ext-gen-warn" *ngIf="showMissingBuyerEmail">
          <i class="fa-solid fa-triangle-exclamation"></i>
          This buyer has no email address on record. A one-time passcode cannot be sent until one is added.
        </small>
      </div>

      <div class="ext-gen-field">
        <label>Extra fields for this link <span class="text-muted">(optional)</span></label>
        <!-- On top of the project's standard template — for the one buyer who needs something
             extra (another document, a one-off question) without changing the form everyone else
             gets. Picked from the same catalogue as the project's Settings tab, minus whatever is
             already on the template. -->
        <ng-select
          class="ext-gen-select"
          [items]="availableExtraFields"
          bindLabel="label"
          bindValue="key"
          [multiple]="true"
          [closeOnSelect]="false"
          [searchable]="true"
          placeholder="Add a field just for this buyer…"
          [(ngModel)]="selectedExtraKeys">
        </ng-select>
        <small class="ext-gen-hint" *ngIf="selectedExtraKeys.length"
          >Shown only on this buyer's form, in addition to the project's standard fields.</small
        >
      </div>

      <div class="ext-gen-field">
        <!-- No hardcoded amount: the deposit differs per project. -->
        <label>Has the initial deposit been received?</label>
        <div class="ext-gen-toggle">
          <button type="button" [class.is-active]="depositReceived === true" (click)="depositReceived = true">Yes</button>
          <button type="button" [class.is-active]="depositReceived === false" (click)="depositReceived = false">No</button>
        </div>
        <small class="ext-gen-hint" *ngIf="depositReceived === true">The lot can be reserved once the deposit is confirmed.</small>
        <small class="ext-gen-warn" *ngIf="depositReceived === false">
          <i class="fa-solid fa-circle-info"></i>
          The lot remains available for sale, and the buyer is advised that submitting an EOI does not reserve or secure the property.
        </small>
      </div>

      <div class="ext-gen-field">
        <label>Link Expiry</label>
        <p-calendar [firstDayOfWeek]="1" dateFormat="dd/mm/yy" [showIcon]="true" appendTo="body" [(ngModel)]="genExpiryDateValue"></p-calendar>
      </div>
    </div>

    <div class="ext-gen-modal__foot">
      <button type="button" class="ext-gen-btn ext-gen-btn--ghost" (click)="close()">Cancel</button>
      <button type="button" class="ext-gen-btn ext-gen-btn--primary" (click)="generate()" [disabled]="generating">
        {{ submitLabel }}
      </button>
    </div>

    <!--
      Confirmation for a buyer with no email address. Covers the dialog rather than sitting inside
      it so the decision cannot be scrolled past — the resulting link is unusable, not just
      imperfect, and that is worth a deliberate answer.
    -->
    <div class="ext-gen-confirm" *ngIf="showNoEmailConfirm">
      <div class="ext-gen-confirm__panel" role="alertdialog" aria-labelledby="extGenConfirmTitle">
        <div class="ext-gen-confirm__icon"><i class="fa-solid fa-envelope-circle-check"></i></div>
        <h4 id="extGenConfirmTitle">This buyer has no email address</h4>
        <p>
          Access to the external EOI form is protected by a one-time passcode, and that passcode can only be sent by email. Without an address on file
          the buyer will not be able to open this link.
        </p>
        <p class="ext-gen-confirm__hint">Add an email address to the contact first, or generate the link now and share it once one has been added.</p>
        <div class="ext-gen-confirm__actions">
          <button type="button" class="ext-gen-btn ext-gen-btn--primary" (click)="cancelGenerateWithoutEmail()">Go back and add an email</button>
          <button type="button" class="ext-gen-btn ext-gen-btn--ghost" (click)="confirmGenerateWithoutEmail()">Generate anyway</button>
        </div>
      </div>
    </div>
  </div>
</div>
